import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { CompanySearch } from "@/components/CompanySearch";
import { firestoreService } from "@/services/firestoreService";
import { MarketTicker } from "@/components/MarketTicker";

interface Ranking {
  id: string;
  name: string;
  ticker?: string;
  score: number;
  type: string;
}

interface MarketMover {
  id: string;
  ticker: string;
  companyName: string;
  changePercent: number;
  type: "gainer" | "loser";
}

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const [topCompanies, setTopCompanies] = useState<Ranking[]>([]);
  const [topCEOs, setTopCEOs] = useState<Ranking[]>([]);
  const [topGainers, setTopGainers] = useState<MarketMover[]>([]);
  const [topLosers, setTopLosers] = useState<MarketMover[]>([]);
  const [totalCompanies, setTotalCompanies] = useState<number>(0);
  const [totalGainers, setTotalGainers] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const popularTickers = [
    "AAPL",
    "MSFT",
    "AMZN",
    "GOOGL",
    "TSLA",
    "NVDA",
    "META",
  ];

  useEffect(() => {
    loadHomepageData();
  }, []);

  const loadHomepageData = async () => {
    try {
      setLoading(true);

      // Use Promise.allSettled to handle individual query failures gracefully
      const results = await Promise.allSettled([
        firestoreService.getDocuments<Ranking>("rankings", [
          firestoreService.where("type", "==", "competitive"),
          firestoreService.orderBy("score", "desc"),
          firestoreService.limit(10),
        ]),
        firestoreService.getDocuments<Ranking>("rankings", [
          firestoreService.where("type", "==", "influential"),
          firestoreService.orderBy("score", "desc"),
          firestoreService.limit(10),
        ]),
        firestoreService.getDocuments<MarketMover>("marketMovers", [
          firestoreService.where("type", "==", "gainer"),
          firestoreService.orderBy("changePercent", "desc"),
          firestoreService.limit(5),
        ]),
        firestoreService.getDocuments<MarketMover>("marketMovers", [
          firestoreService.where("type", "==", "loser"),
          firestoreService.orderBy("changePercent", "asc"),
          firestoreService.limit(5),
        ]),
        // Get total company count (limited to 1000 for performance)
        firestoreService.getDocuments<{ id: string }>("companies", [
          firestoreService.limit(1000),
        ]),
        // Get total gainers count
        firestoreService.getDocuments<MarketMover>("marketMovers", [
          firestoreService.where("type", "==", "gainer"),
        ]),
      ]);

      // Extract results, using empty arrays on failure
      const companies =
        results[0].status === "fulfilled" ? results[0].value : [];
      const ceos = results[1].status === "fulfilled" ? results[1].value : [];
      const gainers = results[2].status === "fulfilled" ? results[2].value : [];
      const losers = results[3].status === "fulfilled" ? results[3].value : [];
      const allCompanies =
        results[4].status === "fulfilled" ? results[4].value : [];
      const allGainers =
        results[5].status === "fulfilled" ? results[5].value : [];

      // Log any failures
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(`Query ${index} failed:`, result.reason);
        }
      });

      setTopCompanies(companies);
      setTopCEOs(ceos);
      setTopGainers(gainers);
      setTopLosers(losers);
      setTotalCompanies(allCompanies.length);
      setTotalGainers(allGainers.length);
    } catch (error) {
      console.error("Failed to load homepage data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Market Ticker */}
      <div className="sticky top-0 z-50">
        <MarketTicker />
      </div>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="h1 text-5xl md:text-6xl mb-6"
              style={{ color: "#76B900" }}
            >
              AI-Powered FinTech Intelligence
            </h1>
            <p className="body-text text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Analyze earnings calls, financial reports, and market data with
              deep AI insights. Make informed investment decisions faster.
            </p>

            {/* Company Search */}
            <div className="mb-8 max-w-2xl mx-auto">
              <CompanySearch placeholder="Search for any company..." />
            </div>

            <div className="flex gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate("/signup")}
              >
                Get Started Free
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate("/companies")}
              >
                Explore Companies
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      {!loading && (
        <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blazerex-green rounded-lg flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-blazerex-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="body-text text-sm text-gray-600">
                      Companies Tracked
                    </p>
                    <p className="h2 text-2xl text-blazerex-green">
                      {loading
                        ? "Loading..."
                        : totalCompanies > 0
                        ? `${totalCompanies}+`
                        : "0"}
                    </p>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blazerex-green rounded-lg flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-blazerex-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="body-text text-sm text-gray-600">
                      Top Gainers
                    </p>
                    <p className="h2 text-2xl text-green-600">
                      {loading
                        ? "Loading..."
                        : totalGainers > 0
                        ? totalGainers
                        : topGainers.length}
                    </p>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blazerex-green rounded-lg flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-blazerex-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="body-text text-sm text-gray-600">Rankings</p>
                    <p className="h2 text-2xl text-blazerex-green">
                      {loading
                        ? "Loading..."
                        : topCompanies.length + topCEOs.length > 0
                        ? topCompanies.length + topCEOs.length
                        : "0"}
                    </p>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blazerex-green rounded-lg flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-blazerex-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="body-text text-sm text-gray-600">
                      Real-time Data
                    </p>
                    <p className="h2 text-2xl text-blazerex-green">Live</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Quick Access Companies */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <h2 className="h2 text-2xl mb-6 text-center flex items-center justify-center gap-2">
            <svg
              className="w-6 h-6 text-blazerex-green"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            Quick Access
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {popularTickers.map((ticker) => (
              <Button
                key={ticker}
                variant="outline"
                onClick={() => navigate(`/companies/${ticker}`)}
                className="font-mono"
              >
                {ticker}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Rankings Preview */}
      {!loading && (topCompanies.length > 0 || topCEOs.length > 0) && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="h2 text-3xl flex items-center gap-2">
                <svg
                  className="w-7 h-7 text-blazerex-green"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
                Top Rankings
              </h2>
              <Button variant="outline" onClick={() => navigate("/rankings")}>
                View All →
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {topCompanies.length > 0 && (
                <Card>
                  <h3 className="sub-header text-xl mb-4 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-blazerex-green"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                      />
                    </svg>
                    Top Companies
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Rank
                          </th>
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Company
                          </th>
                          <th className="text-right py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCompanies.slice(0, 10).map((company, index) => (
                          <tr
                            key={company.id}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() =>
                              company.ticker &&
                              navigate(`/companies/${company.ticker}`)
                            }
                          >
                            <td className="py-3 px-2">
                              <span className="body-text font-mono text-gray-500">
                                {index + 1}
                              </span>
                            </td>
                            <td className="py-3 px-2">
                              <div>
                                <p className="body-text font-medium">
                                  {company.name}
                                </p>
                                {company.ticker && (
                                  <p className="body-text text-sm text-gray-500 font-mono">
                                    {company.ticker}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className="body-text text-blazerex-green font-medium">
                                {company.score.toFixed(1)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              {topCEOs.length > 0 && (
                <Card>
                  <h3 className="sub-header text-xl mb-4 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-blazerex-green"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Top CEOs
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Rank
                          </th>
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            CEO / Company
                          </th>
                          <th className="text-right py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCEOs.slice(0, 10).map((ceo, index) => (
                          <tr
                            key={ceo.id}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() =>
                              ceo.ticker && navigate(`/companies/${ceo.ticker}`)
                            }
                          >
                            <td className="py-3 px-2">
                              <span className="body-text font-mono text-gray-500">
                                {index + 1}
                              </span>
                            </td>
                            <td className="py-3 px-2">
                              <div>
                                <p className="body-text font-medium">
                                  {ceo.name}
                                </p>
                                {ceo.ticker && (
                                  <p className="body-text text-sm text-gray-500 font-mono">
                                    {ceo.ticker}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className="body-text text-blazerex-green font-medium">
                                {ceo.score.toFixed(1)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Market Movers Preview */}
      {!loading && (topGainers.length > 0 || topLosers.length > 0) && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="h2 text-3xl flex items-center gap-2">
                <svg
                  className="w-7 h-7 text-blazerex-green"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                Market Movers
              </h2>
              <Button
                variant="outline"
                onClick={() => navigate("/market-movers")}
              >
                View All →
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {topGainers.length > 0 && (
                <Card>
                  <h3 className="sub-header text-xl mb-4 text-green-600 flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    Top Gainers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Ticker
                          </th>
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Company
                          </th>
                          <th className="text-right py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Change
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topGainers.map((mover) => (
                          <tr
                            key={mover.id}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() =>
                              navigate(`/companies/${mover.ticker}`)
                            }
                          >
                            <td className="py-3 px-2">
                              <p className="body-text font-medium font-mono">
                                {mover.ticker}
                              </p>
                            </td>
                            <td className="py-3 px-2">
                              <p className="body-text text-sm text-gray-500">
                                {mover.companyName}
                              </p>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className="body-text text-green-600 font-medium">
                                +{mover.changePercent.toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
              {topLosers.length > 0 && (
                <Card>
                  <h3 className="sub-header text-xl mb-4 text-red-600 flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                      />
                    </svg>
                    Top Losers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Ticker
                          </th>
                          <th className="text-left py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Company
                          </th>
                          <th className="text-right py-2 px-2 body-text text-sm text-gray-600 font-medium">
                            Change
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topLosers.map((mover) => (
                          <tr
                            key={mover.id}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() =>
                              navigate(`/companies/${mover.ticker}`)
                            }
                          >
                            <td className="py-3 px-2">
                              <p className="body-text font-medium font-mono">
                                {mover.ticker}
                              </p>
                            </td>
                            <td className="py-3 px-2">
                              <p className="body-text text-sm text-gray-500">
                                {mover.companyName}
                              </p>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <span className="body-text text-red-600 font-medium">
                                {mover.changePercent.toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Feature Showcase Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="h2 text-4xl text-center mb-4">Powerful AI Features</h2>
          <p className="body-text text-xl text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Everything you need to make informed investment decisions, powered by advanced AI
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card hover className="text-center">
              <div className="w-16 h-16 bg-blazerex-green rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blazerex-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="sub-header text-xl mb-2">ECT Analysis</h3>
              <p className="body-text text-gray-600 text-sm">
                6 comprehensive modules: Summary, Risks & Opportunities, Key Numbers, Strategy, Sentiment, and Q&A
              </p>
            </Card>

            <Card hover className="text-center">
              <div className="w-16 h-16 bg-blazerex-green rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blazerex-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
              </div>
              <h3 className="sub-header text-xl mb-2">ECT Chat</h3>
              <p className="body-text text-gray-600 text-sm">
                Ask questions about earnings calls, compare quarters, and get AI-powered insights instantly
              </p>
            </Card>

            <Card hover className="text-center">
              <div className="w-16 h-16 bg-blazerex-green rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blazerex-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
              </div>
              <h3 className="sub-header text-xl mb-2">ECT Mindmap</h3>
              <p className="body-text text-gray-600 text-sm">
                Visual mindmap showing relationships between key concepts, metrics, risks, and opportunities
              </p>
            </Card>

            <Card hover className="text-center" onClick={() => navigate("/rankings")}>
              <div className="w-16 h-16 bg-blazerex-green rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blazerex-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
              </div>
              <h3 className="sub-header text-xl mb-2">Rankings</h3>
              <p className="body-text text-gray-600 text-sm">
                Top companies, influential CEOs, and polarizing stocks ranked by AI-analyzed metrics
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="h2 text-4xl text-center mb-12">Complete Financial Intelligence</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card hover onClick={() => navigate("/filings")}>
              <div className="text-center">
                <div className="w-16 h-16 bg-blazerex-green rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-blazerex-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="sub-header text-xl mb-2">Financial Reports</h3>
                <p className="body-text text-gray-600">
                  Deep analysis of 10-K, 10-Q filings with key metrics, YoY changes, and forward-looking statements.
                </p>
              </div>
            </Card>

            <Card hover>
              <div className="text-center">
                <div className="w-16 h-16 bg-blazerex-green rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-blazerex-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
                <h3 className="sub-header text-xl mb-2">Market Movers</h3>
                <p className="body-text text-gray-600">
                  Real-time tracking of biggest gainers, losers, and market trends with AI commentary.
                </p>
              </div>
            </Card>

            <Card hover>
              <div className="text-center">
                <div className="w-16 h-16 bg-blazerex-green rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-blazerex-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                  </svg>
                </div>
                <h3 className="sub-header text-xl mb-2">News & Insights</h3>
                <p className="body-text text-gray-600">
                  AI-summarized financial news with company-specific filtering and sentiment analysis.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blazerex-green">
        <div className="container mx-auto px-4 text-center">
          <h2 className="h2 text-4xl text-blazerex-black mb-6">
            Ready to Get Started?
          </h2>
          <p className="body-text text-xl text-blazerex-black mb-8 max-w-2xl mx-auto">
            Join thousands of investors using AI to make better decisions.
          </p>
          <Button
            variant="outline"
            size="lg"
            onClick={() => (window.location.href = "/signup")}
            className="bg-white border-blazerex-black text-blazerex-black hover:bg-gray-100"
          >
            Start Free Trial
          </Button>
        </div>
      </section>
    </div>
  );
};
